'use client';

import { useEffect } from 'react';
import useUser from '@alltrails/context/hooks/useUser';
import useViewedLocationPage from '@alltrails/core/hooks/useViewedLocationPage';
import { get } from '@alltrails/core/utils/get';
import logError from '@alltrails/core/utils/logError';
import PageViewLimit from '@alltrails/trails/components/PageViewLimit/PageViewLimit';
import { pageViewLimit } from '@alltrails/trails/utils/constants';
import type { TrailId } from '@alltrails/types/TrailId';
import ScrollLimit from '@alltrails/user-auth/components/ScrollLimit/ScrollLimit';
import useIsSeoOrViralityTraffic from '@/hooks/useIsSeoOrViralityTraffic';
import usePageViewLimit from '@/hooks/usePageViewLimit';
import useScrollAuthAltExposure from '@/hooks/useScrollAuthAltExposure';
import useScrollAuthAltTreatment from '@/hooks/useScrollAuthAltTreatment';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import { setViewLimitReached } from '@/utils/trailPageSlice';
import ScrollAuthAltTreatments from '../ScrollAuthAltTreaments/ScrollAuthAltTreatments';

type ViewLimitHandlerProps = {
  pageReferer: string | null;
  trailId: TrailId;
  isBot?: boolean;
};

const ViewLimitHandler = ({ pageReferer, trailId, isBot = false }: ViewLimitHandlerProps) => {
  const user = useUser();
  const dispatch = useAppDispatch();
  const limitReached = useAppSelector(state => state.trailPage.viewLimitReached);
  const isMobile = useAppSelector(state => !!state.context?.mobileBrowser);
  const { hasViewedLocationPage } = useViewedLocationPage();

  useEffect(() => {
    const setViewLimit = async () => {
      if (user) {
        return;
      }

      try {
        const { limitReached } = await get<{ limitReached: boolean }>(`/api/mugen/update-view-limit`, {
          headers: {
            ...(!!pageReferer ? { 'X-Page-Referer': pageReferer } : {})
            // header to always show reg wall when referred from an external site
            // { 'X-External-Wall': true }
          }
        });
        dispatch(setViewLimitReached(limitReached));
      } catch (e: any) {
        logError(e);
      }
    };

    setViewLimit();
  }, [user]);

  // Scroll auth alt XP logic
  // User must be unauthenticated and on a mobile device to be eligible for a treatment.
  // If they are bucketed into a (non-control) treatment, render it; otherwise fall through
  // to the default scroll/page-view limit logic below.
  const { isEligible, isInTreatment, treatmentValue } = useScrollAuthAltTreatment(isBot);
  const isSeoOrViralityTraffic = useIsSeoOrViralityTraffic();
  useScrollAuthAltExposure(isBot);

  // Never gate bot traffic behind the scroll limit so crawlers can read full content.
  const enableScrollLimit = !isBot && (isSeoOrViralityTraffic || hasViewedLocationPage);

  const isAuthAltTreatmentEligible = isEligible && isInTreatment;
  const { hasReachedPageLimit } = usePageViewLimit(isAuthAltTreatmentEligible);

  if (isAuthAltTreatmentEligible) {
    return <ScrollAuthAltTreatments hasReachedPageLimit={hasReachedPageLimit} trailId={trailId} treatmentValue={treatmentValue} />;
  }

  // Prevents legacy walls if a mobile, non-bot user is bucketed into a scroll-auth treatment;
  // avoids re-gating after login. Desktop users, control group, and cypress tests see legacy flow.
  if (!isBot && isMobile && isInTreatment) {
    return null;
  }

  return enableScrollLimit ? (
    <ScrollLimit deeplinkData={{ pageType: 'trail', pageId: trailId }} />
  ) : (
    <PageViewLimit limit={pageViewLimit} limitReached={limitReached === 'uninitialized' ? false : limitReached} trailId={trailId} />
  );
};

export default ViewLimitHandler;
